import type { FirePost } from "../types/models";
import type {
  AccountType,
  IdentityEntitlements,
  LegacyAccountType,
  VerificationStatus,
  PublicIdentitySnapshot,
} from "../types/identity";
import { getPublicAuthorSnapshot } from "./identity";
import { getPostImageUrls } from "./postImages";

const MAX_SHARED_POST_IMAGE_URL_LENGTH = 2048;

export type SharedPostPayload = {
  postId: string;
  authorIdentityId?: string;
  authorSnapshot?: PublicIdentitySnapshot;
  authorUsername?: string;
  authorEmail?: string;
  authorAccountType?: AccountType;
  authorVerificationStatus?: VerificationStatus;
  authorEntitlements?: IdentityEntitlements;
  /** @deprecated Compatibility snapshot for historical student/teacher posts. */
  authorRole?: LegacyAccountType;
  subject?: string;
  text?: string;
  groupName?: string;
  imageUrl?: string;
  attachmentType?: "image" | "pdf";
  attachmentName?: string;
};

function cleanOptionalString(value: unknown, maxLength: number) {
  if (typeof value !== "string") return "";
  const clean = value.trim();
  return clean.length <= maxLength ? clean : clean.slice(0, maxLength);
}

function cleanAttachmentType(value: unknown): "image" | "pdf" | undefined {
  return value === "image" || value === "pdf" ? value : undefined;
}

export function buildSharedPostPayload(post: FirePost): SharedPostPayload {
  const rawImageUrl = getPostImageUrls(post)[0] ?? "";
  const imageUrl = cleanOptionalString(rawImageUrl, MAX_SHARED_POST_IMAGE_URL_LENGTH);
  const attachmentType = cleanAttachmentType(post.attachmentType);
  const authorSnapshot = post.authorIdentityId && post.authorSnapshot
    ? getPublicAuthorSnapshot(post)
    : null;
  const authorIdentityId = authorSnapshot ? post.authorIdentityId : undefined;

  return {
    postId: post.id,
    ...(authorIdentityId ? { authorIdentityId } : {}),
    ...(authorSnapshot ? { authorSnapshot } : {}),
    authorUsername: cleanOptionalString(post.authorUsername, 32),
    authorEmail: cleanOptionalString(post.authorEmail, 320),
    authorAccountType: post.authorAccountType,
    authorVerificationStatus: post.authorVerificationStatus,
    authorEntitlements: post.authorEntitlements,
    authorRole: post.authorRole,
    subject: cleanOptionalString(post.subject, 64),
    text: cleanOptionalString(post.text, 3000),
    groupName: cleanOptionalString(post.groupName, 60),
    imageUrl,
    attachmentType,
    attachmentName: cleanOptionalString(post.attachmentName, 200),
  };
}
